import React, { useState, useEffect, useCallback, useRef } from 'react';
import naviaLogo from '../assets/navia-logo.png';

/* ── Google Font ────────────────────────────────────────────────────────────*/
const _fl = document.createElement('link');
_fl.rel  = 'stylesheet';
_fl.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap';
if (!document.head.querySelector('[href*="DM+Sans"]')) document.head.appendChild(_fl);

/* ── Navia Logo ─────────────────────────────────────────────────────────────*/


/* ── Helpers ────────────────────────────────────────────────────────────────*/
function fmtIST(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString('en-IN', {
    timeZone:'Asia/Kolkata', day:'2-digit', month:'short',
    hour:'2-digit', minute:'2-digit', hour12:true
  });
}
function fmtQty(v) { return v == null ? '0' : Number(v).toString(); }
// Matches client Dashboard.jsx's formatStrike() exactly -- plain rounded
// integer, no thousands separator. e.g. 25000, not 25,000.
function formatStrike(v) { if (v === null || v === undefined || v === '') return null; return String(Math.round(Number(v))); }
// Traded price formatter -- same ₹/en-IN convention as client Dashboard.jsx's
// equivalent helper, so both views show the trade price identically. Returns
// '—' when no trade price exists yet (order not traded).
function fmtPrice(v) { if (v===null||v===undefined||v==='') return '—'; return `₹${Number(v).toLocaleString('en-IN',{maximumFractionDigits:2})}`; }

// IST-aware "is this timestamp today" check. Used to scope order-status
// matching to TODAY only -- without this, an old order from a previous day
// (e.g. yesterday's test square-off that later got marked ORDER_TRADED) can
// match a brand-new, never-touched position purely because it shares the
// same symbol/exchange/segment/contract, incorrectly showing "Traded" for a
// position the client never actually squared off today.
function isToday(dateVal) {
  if (!dateVal) return false;
  const d       = new Date(dateVal);
  const nowIST  = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  const dIST    = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  return dIST.getUTCFullYear() === nowIST.getUTCFullYear()
      && dIST.getUTCMonth()    === nowIST.getUTCMonth()
      && dIST.getUTCDate()     === nowIST.getUTCDate();
}

// Exchange badge -- same color convention as the client Dashboard.jsx /
// DealerClientView.jsx so the dealer view visually matches what the client
// sees for the same position, instead of showing exchange as plain text.
const EX = {
  NSE: { bg:'#eff6ff', color:'#1d4ed8', border:'#bfdbfe' },
  BSE: { bg:'#f0fdf4', color:'#15803d', border:'#bbf7d0' },
  MCX: { bg:'#fff7ed', color:'#c2410c', border:'#fed7aa' },
};
function ExBadge({ exchange, segment }) {
  const ex = EX[exchange] || { bg:'#f3f4f6', color:'#374151', border:'#e5e7eb' };
  return <span style={{ fontSize:9, fontWeight:600, padding:'2px 6px', borderRadius:4, background:ex.bg, color:ex.color, border:`0.5px solid ${ex.border}`, whiteSpace:'nowrap' }}>{exchange}{segment?` ${segment}`:''}</span>;
}

function authHeader() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('nb_dealer_token') || ''}`
  };
}

/* ── Status badge — mirrors Dashboard.jsx (client view) exactly ─────────────
 * FILE_GENERATED is admin-internal — dealer still sees it as "Order received"
 * for consistency with what the client sees, avoiding confusion when a dealer
 * cross-checks what the client is seeing on their own login. */
function statusBadge(status) {
  const s = (status || '').toUpperCase();
  if (s === 'ORDER_RECEIVED' || s === 'RECEIVED' || s === 'FILE_GENERATED')
    return { label:'Order received',   bg:'#EEF2FF', col:'#1338A8' };
  if (s === 'PARTIALLY_TRADED')
    return { label:'Partially traded', bg:'#FFFBEB', col:'#92400E' };
  if (s === 'ORDER_TRADED' || s === 'TRADED')
    return { label:'Traded',           bg:'#E1F5EE', col:'#085041' };
  if (s === 'REJECTED')
    return { label:'Rejected',         bg:'#FEF2F2', col:'#991B1B' };
  return { label:'Order received', bg:'#EEF2FF', col:'#1338A8' };
}

// Classify a holding as squareoff-eligible plain equity vs everything else
// (bonds, SGBs, ETFs, InvIT/REIT units, mutual funds) -- same classifier as
// client Dashboard.jsx, so both views agree on what's squareoff-able.
function isEquityHolding(compname, isin) {
  const isinU = (isin || '').toUpperCase().trim();
  const nameU = (compname || '').toUpperCase();
  if (isinU.startsWith('INF')) return false;
  if (isinU.startsWith('IN0')) return false;
  const looksEquity    = /\bEQ\.?\b/.test(nameU);
  const looksNonEquity = /MUTUAL FUND|\bETF\b|\bINVIT\b|\bTRUST\b|\bBD\b|\bBOND\b|\bSGB\b|\bT-BILL\b|\bSDL\b/.test(nameU);
  return looksEquity && !looksNonEquity;
}

/* ═══════════════════════════════════════════════════════════════════════════*/
export default function DealerDashboard({ dealer, onLogout }) {

  /* ── View state: 'search' | 'client' ─────────────────────────────────────*/
  const [view,          setView]          = useState('search');
  const [clientData,    setClientData]    = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);

  /* ── Search state ─────────────────────────────────────────────────────────*/
  const [ucc,         setUcc]         = useState('');
  const [searchError, setSearchError] = useState('');
  const [recentList,  setRecentList]  = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  /* ── Client view state ───────────────────────────────────────────────────*/
  const [activeTab,    setActiveTab]   = useState('positions');
  const [placing,      setPlacing]     = useState(false);
  const [confirmModal, setConfirmModal]= useState(null);
  // Optimistic "just placed" flags -- mirror client Dashboard.jsx's
  // placedFO/placedDayEquity/placedHoldings exactly. Set immediately after a
  // successful square-off response so the row's action cell flips right away,
  // instead of waiting for refreshClientData() (a much heavier request) to
  // resolve and replace clientData before the UI can show anything changed.
  const [placedFO,        setPlacedFO]        = useState({});
  const [placedDayEquity, setPlacedDayEquity] = useState({});
  const [placedHoldings,  setPlacedHoldings]  = useState({});
  const [message,      setMessage]     = useState('');
  const [msgType,      setMsgType]     = useState('success');

  // NEW: "Square off all" (Net + Day tabs) state -- batch confirmation modal,
  // per-tab in-flight flag, and per-tab live progress counter. Kept separate
  // from the single-order confirmModal/placing state above so the existing
  // single square-off flow is completely untouched by this addition.
  const [bulkConfirmModal, setBulkConfirmModal] = useState(null);        // { kind:'net'|'day', rows:[...] }
  const [bulkPlacing,      setBulkPlacing]      = useState({ net:false, day:false });
  const [bulkProgress,     setBulkProgress]     = useState({ net:null,  day:null  }); // { done, total }

  const dealerToken = localStorage.getItem('nb_dealer_token') || '';

  /* ── Load recent clients from DB on mount ─────────────────────────────────*/
  useEffect(() => {
    fetch('/api/dealer/recent-clients', { headers: authHeader() })
      .then(r => {
        // On 401 just show empty list — don't log out (token may be in flight)
        if (!r.ok) return { success: true, clients: [] };
        return r.json();
      })
      .then(d => {
        if (d?.success) setRecentList(d.clients || []);
      })
      .catch(() => {})
      .finally(() => setLoadingRecent(false));
  }, []);

  /* ── Session expiry handler ──────────────────────────────────────────────*/
  const handleExpiry = useCallback(() => {
    localStorage.removeItem('nb_dealer_token');
    localStorage.removeItem('nb_dealer');
    onLogout();
  }, [onLogout]);

  /* ── Load client data (same page) ────────────────────────────────────────*/
  const loadClient = useCallback(async (targetUCC) => {
    const uccClean = (targetUCC || ucc).trim().toUpperCase();
    if (!uccClean) { setSearchError('Please enter a UCC.'); return; }

    setLoadingClient(true);
    setSearchError('');
    try {
      const r = await fetch('/api/dealer/client-data', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ ucc: uccClean })
      });

      if (r.status === 401) { handleExpiry(); return; }

      const d = await r.json();
      if (!d.success) {
        setSearchError(d.error || 'Client not found.');
        return;
      }

      setClientData(d);
      setView('client');
      setActiveTab('positions');
      setUcc('');
      setMessage('');
      // Reset optimistic placement state -- must not leak between clients.
      // Two different UCCs can share an identical-looking contract key (e.g.
      // both trading NIFTY 25000 CE); without this a stale flag from the
      // PREVIOUS client would wrongly show "Order placed" on the new client's
      // fresh, untouched row.
      setPlacedFO({});
      setPlacedDayEquity({});
      setPlacedHoldings({});

    } catch (err) {
      setSearchError('Network error. Please try again.');
    } finally {
      setLoadingClient(false);
    }
  }, [ucc, handleExpiry]);

  /* ── Refresh client data ─────────────────────────────────────────────────
   * silent=true is used by the background auto-refresh interval below -- it
   * updates clientData without touching loadingClient, so the UI never shows
   * a loading flicker every 90s (mirrors client Dashboard.jsx's firstLoadRef
   * pattern, which only shows a spinner on the very first load). */
  const refreshClientData = useCallback(async (silent = false) => {
    if (!clientData?.client?.ucc) return;
    if (!silent) setLoadingClient(true);
    try {
      const r = await fetch('/api/dealer/client-data', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ ucc: clientData.client.ucc })
      });
      if (r.status === 401) { handleExpiry(); return; }
      const d = await r.json();
      if (d.success) setClientData(d);
    } catch {}
    finally { if (!silent) setLoadingClient(false); }
  }, [clientData, handleExpiry]);

  /* ── Silent auto-refresh — matches client Dashboard.jsx's 90s background
   * poll exactly. Only runs while a client is loaded in this page; cleaned
   * up on view change / client switch / unmount. */
  useEffect(() => {
    if (view !== 'client' || !clientData?.client?.ucc) return;
    const iv = setInterval(() => { refreshClientData(true); }, 90000);
    return () => clearInterval(iv);
  }, [view, clientData?.client?.ucc, refreshClientData]);

  /* ── Place sq-off ────────────────────────────────────────────────────────*/
  const placeSquareOff = useCallback(async () => {
    if (!confirmModal) return;
    setPlacing(true);
    try {
      const r = await fetch('/api/dealer/place-squareoff', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          ucc:          clientData.client.ucc,
          exchange:     confirmModal.exchange,
          segment:      confirmModal.segment,
          symbol:       confirmModal.symbol,
          quantity:     confirmModal.quantity,
          side:         confirmModal.side,
          expiry_date:  confirmModal.expiry_date  || null,
          strike_price: confirmModal.strike_price || null,
          option_type:  confirmModal.option_type  || null,
        })
      });
      if (r.status === 401) { handleExpiry(); return; }
      const d = await r.json();

      if (d.success) {
        setMessage(`Square-off placed for ${confirmModal.symbol}. Order: ${d.orderId}`);
        setMsgType('success');
        // FIX (17-Jul-2026): optimistic local state update, mirroring client
        // Dashboard.jsx's confirmSquareOff() exactly. The 14-Jul-2026 fix
        // below already stopped refreshClientData() from BLOCKING the modal
        // close/spinner, but the row itself still only flipped from "Square
        // off" to "Order received" once that full /api/dealer/client-data
        // refetch resolved -- and that request is much heavier than the
        // client's own background refresh (it pulls positions/orders/
        // day_positions/bf_positions plus a live Sharepro holdings call).
        // Setting the flag here, from data we already have locally, makes
        // the row update instantly, same as the client dashboard, while
        // refreshClientData() below still runs to reconcile with the server.
        if (confirmModal.isHolding) {
          setPlacedHoldings(p => ({ ...p, [confirmModal.isin]: true }));
        } else if (confirmModal.isDayEquity) {
          setPlacedDayEquity(p => ({ ...p, [confirmModal.dayKey]: true }));
        } else if (confirmModal.foKey) {
          setPlacedFO(p => ({ ...p, [confirmModal.foKey]: true }));
        }
        setConfirmModal(null);
        // FIX (14-Jul-2026): was `await`ed here, which kept the modal's
        // "Placing..." state (and setPlacing(false) in the finally block
        // below) blocked on a full client-data refetch -- a much heavier
        // request than the placement call itself, making the dealer
        // terminal feel noticeably slower than the client app for the exact
        // same action (the client optimistically updates its own local
        // state immediately after placement, without waiting on a full
        // reload). Firing this in the background instead lets the UI
        // unblock immediately; the table still updates a moment later once
        // this resolves, same as before, it just no longer holds up the
        // Confirm button/spinner.
        refreshClientData();
      } else {
        setMessage(d.message || d.error || 'Failed to place order.');
        setMsgType('error');
        setConfirmModal(null);
      }
    } catch (err) {
      setMessage('Network error. Please retry.');
      setMsgType('error');
      setConfirmModal(null);
    } finally {
      setPlacing(false);
      setTimeout(() => setMessage(''), 6000);
    }
  }, [confirmModal, clientData, handleExpiry, refreshClientData]);

  /* ── Check if order already placed (from server data) ───────────────────*/
  function isPlaced(symbol, exchange, segment, expiryDate, strikePrice, optionType) {
    if (!clientData?.orders) return false;
    // Only block sq-off button for ACTIVE orders (not already traded/rejected).
    // FIX (14-Jul-2026): FILE_GENERATED was missing here -- once admin
    // generates the RMS file for an order, its status moves from
    // ORDER_RECEIVED to FILE_GENERATED, which this whitelist didn't cover.
    // Net/Day tabs have their own explicit FILE_GENERATED branch before ever
    // reaching this function's result, so they were unaffected -- but the
    // Holdings tab calls isPlaced() directly with no such branch, so its
    // Square-off button was reverting to clickable the moment a file was
    // generated, letting the same holding be square-off'd again.
    const activeStatuses = ['ORDER_RECEIVED','RECEIVED','FILE_GENERATED','PARTIALLY_TRADED'];
    return clientData.orders.some(o => {
      if (o.symbol !== symbol || o.exchange !== exchange || o.segment !== segment) return false;
      // For FAO, also match the exact contract — different strike/expiry/option_type
      // is a DIFFERENT position, not the same one. Without this, an unrelated order
      // on the same underlying symbol incorrectly affects every other contract.
      if (expiryDate !== undefined) {
        const sameExpiry = (o.expiry_date || null) === (expiryDate || null);
        const sameStrike = Number(o.strike_price || 0) === Number(strikePrice || 0);
        const sameOption = (o.option_type || null) === (optionType || null);
        if (!sameExpiry || !sameStrike || !sameOption) return false;
      }
      // Only orders placed TODAY can affect today's position status — an old
      // order from a previous day must never leak into today's fresh position.
      if (!isToday(o.placed_at)) return false;
      return activeStatuses.includes((o.status||'').toUpperCase());
    });
  }

  function getOrderStatus(symbol, exchange, segment, expiryDate, strikePrice, optionType) {
    if (!clientData?.orders) return null;
    return clientData.orders.find(o => {
      if (o.symbol !== symbol || o.exchange !== exchange || o.segment !== segment) return false;
      if (expiryDate !== undefined) {
        const sameExpiry = (o.expiry_date || null) === (expiryDate || null);
        const sameStrike = Number(o.strike_price || 0) === Number(strikePrice || 0);
        const sameOption = (o.option_type || null) === (optionType || null);
        if (!sameExpiry || !sameStrike || !sameOption) return false;
      }
      // Only orders placed TODAY can describe today's position status — an old
      // order from a previous day (even one that finished ORDER_TRADED) must
      // never make a brand-new, untouched position show as "Traded" today.
      if (!isToday(o.placed_at)) return false;
      return true;
    });
  }

  // NEW: single source of truth for what the Net Position row's action cell
  // should show, extracted out of the render IIFE below so "Square off all"
  // can reuse the EXACT same eligibility logic instead of a second,
  // hand-copied version that could quietly drift out of sync with it. The
  // rendered markup for every branch is unchanged -- this is purely an
  // internal refactor of the condition-checking, not a behavior change for
  // any existing single-row flow.
  function netRowAction(p) {
    const netQty = Number(p.net_qty);
    const isFlat = netQty === 0;
    const side   = netQty > 0 ? 'SELL' : 'BUY';
    const qty    = Math.abs(netQty);
    const foKey  = `${p.symbol}|${p.exchange}|${p.segment}|${p.expiry_date||''}|${p.strike_price||''}|${p.option_type||''}`;
    const placed = isPlaced(p.symbol, p.exchange, p.segment, p.expiry_date, p.strike_price, p.option_type) || placedFO[foKey];
    const ord    = getOrderStatus(p.symbol, p.exchange, p.segment, p.expiry_date, p.strike_price, p.option_type);
    const ordSt  = (ord?.status || '').toUpperCase();

    if (isFlat) {
      if (ord && !!ord.file_generated && (ordSt === 'ORDER_TRADED' || ordSt === 'TRADED')) return { type:'traded' };
      return { type:'noOpen' };
    }
    if (ord && !!ord.file_generated && ordSt === 'PARTIALLY_TRADED') return { type:'partiallyTraded' };
    if (ordSt === 'ORDER_RECEIVED' || ordSt === 'RECEIVED' || ordSt === 'FILE_GENERATED') return { type:'orderReceived' };
    if (placed) return { type:'placed' };
    return {
      type: 'squareOff',
      order: {
        symbol: p.symbol, exchange: p.exchange, segment: p.segment, side,
        quantity: qty,
        expiry_date:  p.expiry_date  || null,
        strike_price: p.strike_price || null,
        option_type:  p.option_type  || null,
        foKey,
      }
    };
  }

  // NEW: same idea for the Day/Equity table's action cell.
  function dayEqRowAction(p) {
    const ord      = getOrderStatus(p.symbol, p.exchange, p.segment);
    const ordSt    = (ord?.status || '').toUpperCase();
    const isFlatEQ = Number(p.net_qty) === 0;
    const dayKey   = p.symbol+'_'+p.exchange+'_'+p.segment;

    if (isFlatEQ) {
      if (ord && !!ord.file_generated && (ordSt === 'ORDER_TRADED' || ordSt === 'TRADED')) return { type:'traded' };
      return { type:'noOpen' };
    }
    if (ord && !!ord.file_generated && ordSt === 'PARTIALLY_TRADED') return { type:'partiallyTraded' };
    if (ordSt === 'ORDER_RECEIVED' || ordSt === 'RECEIVED' || ordSt === 'FILE_GENERATED') return { type:'orderReceived' };
    if (isPlaced(p.symbol, p.exchange, p.segment) || placedDayEquity[dayKey]) return { type:'placed' };
    return {
      type: 'squareOff',
      order: {
        symbol: p.symbol, exchange: p.exchange, segment: p.segment,
        side: Number(p.net_qty) > 0 ? 'SELL' : 'BUY',
        quantity: Math.abs(Number(p.net_qty)),
        isDayEquity: true, dayKey,
      }
    };
  }

  // Sort-priority companion to the tables below -- now just maps netRowAction/
  // dayEqRowAction's type onto the same 4-tier scheme as before (0=actionable
  // Square off, 1=in-progress, 2=traded, 3=no open position), so actionable
  // rows still surface first. Numerically identical to the original
  // hand-written priority functions -- only the duplicated condition logic
  // was removed.
  const ACTION_PRIORITY = { squareOff:0, orderReceived:1, partiallyTraded:1, placed:1, traded:2, noOpen:3 };
  function netPositionPriority(p) { return ACTION_PRIORITY[netRowAction(p).type]; }
  function dayEquityPriority(p)   { return ACTION_PRIORITY[dayEqRowAction(p).type]; }

  /* ── Styles ──────────────────────────────────────────────────────────────*/
  const s = {
    page:    { minHeight:'100vh', background:'#F4F6F9', fontFamily:"'DM Sans',-apple-system,sans-serif", fontSize:13, color:'#0D0F12' },
    topbar:  { background:'#fff', borderBottom:'0.5px solid #E2E8F0', padding:'0 24px', height:52, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:50 },
    card:    { background:'#fff', border:'0.5px solid #E2E8F0', borderRadius:12, overflow:'hidden', marginBottom:14 },
    th:      { background:'#F8FAFC', borderBottom:'0.5px solid #E2E8F0', color:'#64748B', fontSize:10, fontWeight:600, padding:'8px 12px', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em', whiteSpace:'nowrap' },
    td:      { color:'#0D0F12', fontSize:12, padding:'9px 12px', verticalAlign:'middle', borderBottom:'0.5px solid #F1F5F9' },
    tabRow:  { display:'flex', background:'#fff', border:'0.5px solid #E2E8F0', borderRadius:8, overflow:'hidden', marginBottom:14 },
    tabPill: (a) => ({ flex:1, border:'none', background:a?'#1B4FD8':'transparent', color:a?'#fff':'#5A6272', padding:'9px 8px', fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }),
    sqBtn:   { background:'#DC2626', color:'#fff', border:'none', borderRadius:6, padding:'6px 12px', fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap' },
    // Universal pill size: Order received / Traded / Partially traded / No open
    // position all now render at the same fixed width (minWidth:110, centered)
    // instead of auto-sizing to their own text length, so the action column
    // doesn't visually jump around between rows of different status.
    placed:  { background:'#FFFBEB', color:'#92400E', border:'0.5px solid #D97706', borderRadius:6, fontSize:10, fontWeight:600, padding:'4px 10px', whiteSpace:'nowrap', display:'inline-block', minWidth:110, textAlign:'center' },
    // Matches client Dashboard.jsx's s.flatDisabled exactly (light theme:
    // surf2 #F0F2F5, tx3 #9BA3B0, bdr rgba(0,0,0,0.08)) -- previously this
    // button used the undefined `s.viewOnly` (never defined anywhere in this
    // file), which silently rendered with no inline style at all.
    flatDisabled: { background:'#F0F2F5', color:'#9BA3B0', border:'0.5px solid rgba(0,0,0,0.08)', borderRadius:6, padding:'6px 12px', fontSize:12, cursor:'not-allowed', fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap', display:'inline-block', minWidth:110, textAlign:'center' },
    // Same fixed-width pill treatment for the Orders tab's Status column badge.
    statusPill: { display:'inline-block', minWidth:110, textAlign:'center', fontSize:10, fontWeight:600, padding:'3px 9px', borderRadius:6 },
    input:   { flex:1, padding:'11px 14px', borderRadius:8, border:'1.5px solid #E2E8F0', background:'#F8FAFC', color:'#0D0F12', fontSize:14, fontWeight:500, outline:'none', fontFamily:"'DM Sans',sans-serif" },
    btnPri:  { padding:'11px 24px', borderRadius:8, border:'none', background:'#1B4FD8', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap' },
    overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
    modal:   { background:'#fff', borderRadius:14, width:'100%', maxWidth:420, border:'0.5px solid #E2E8F0' },
    // NEW: header bar used for the Net/Day tab "Square off all" toolbar --
    // same visual language (padding/border/font) as the existing Day tab's
    // "EQUITY — Cash Market" section header, just widened to a flex row.
    sectionBar: { padding:'9px 14px', borderBottom:'0.5px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'space-between' },
  };

  /* ── Topbar (shared) ─────────────────────────────────────────────────────*/
  const Topbar = () => (
    <header style={s.topbar}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ background:'#fff', borderRadius:7, padding:'3px 10px', boxShadow:'0 1px 4px rgba(0,0,0,.10)', display:'flex', alignItems:'center' }}>
          <img src={naviaLogo} alt='Navia' style={{ height:22, width:'auto', objectFit:'contain' }}/>
        </div>
        <span style={{ fontSize:13, fontWeight:600, color:'#0D0F12' }}>Navia Backup</span>
        <span style={{ color:'#CBD5E0', fontSize:12 }}>|</span>
        <span style={{ fontSize:12, color:'#64748B' }}>Dealer Portal</span>
        {view === 'client' && clientData && (
          <>
            <span style={{ color:'#CBD5E0' }}>›</span>
            <span style={{ fontSize:12, fontWeight:600, color:'#1B4FD8' }}>
              {clientData.client.client_name} ({clientData.client.ucc})
            </span>
          </>
        )}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ background:'#FFF7ED', color:'#C2410C', border:'0.5px solid #FED7AA', padding:'3px 10px', borderRadius:4, fontSize:11, fontWeight:600 }}>
          DEALER: {dealer.dealer_id}
        </span>
        <span style={{ fontSize:12, color:'#64748B' }}>{dealer.name}</span>
        <button onClick={() => { localStorage.removeItem('nb_dealer_token'); localStorage.removeItem('nb_dealer'); onLogout(); }}
          style={{ padding:'5px 12px', borderRadius:6, border:'0.5px solid #E2E8F0', background:'#fff', color:'#64748B', fontSize:11, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
          Sign out
        </button>
      </div>
    </header>
  );

  /* ══════════════════════════════════════════════════════════════════════════
     SEARCH VIEW
  ══════════════════════════════════════════════════════════════════════════*/
  if (view === 'search') return (
    <div style={s.page}>
      <Topbar/>

      <div style={{ maxWidth:600, margin:'40px auto', padding:'0 20px' }}>

        {/* Search card */}
        <div style={{ background:'#fff', border:'0.5px solid #E2E8F0', borderRadius:14, padding:32, marginBottom:20, boxShadow:'0 1px 4px rgba(0,0,0,.04)' }}>
          <div style={{ fontSize:17, fontWeight:600, marginBottom:4 }}>Client UCC Search</div>
          <div style={{ fontSize:12, color:'#64748B', marginBottom:20 }}>
            Enter UCC to load client positions in this page. All sq-off orders are tracked by server.
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <input style={s.input}
              type="text" value={ucc} placeholder="Enter Client UCC e.g. 99405185"
              onChange={e => { setUcc(e.target.value.toUpperCase()); setSearchError(''); }}
              onKeyDown={e => e.key === 'Enter' && loadClient()}
              onFocus={e => e.target.style.borderColor = '#1B4FD8'}
              onBlur={e => e.target.style.borderColor = '#E2E8F0'}
              autoFocus
            />
            <button style={{ ...s.btnPri, opacity: loadingClient || !ucc.trim() ? 0.6 : 1, cursor: loadingClient || !ucc.trim() ? 'not-allowed':'pointer' }}
              onClick={() => loadClient()} disabled={loadingClient || !ucc.trim()}>
              {loadingClient ? 'Loading…' : '→ Load Client'}
            </button>
          </div>

          {searchError && (
            <div style={{ marginTop:10, color:'#DC2626', fontSize:12, background:'#FEF2F2', border:'0.5px solid #FECACA', padding:'8px 12px', borderRadius:6 }}>
              ⚠ {searchError}
            </div>
          )}

          <div style={{ marginTop:14, background:'#F8FAFC', borderRadius:8, padding:'10px 14px', fontSize:11, color:'#64748B', lineHeight:1.7 }}>
            <strong style={{ color:'#374151' }}>Security note:</strong> Client data loads in this page. Sq-off state is always verified from server — switching between clients shows accurate order status from database.
          </div>
        </div>

        {/* Recent clients */}
        {!loadingRecent && recentList.length > 0 && (
          <div style={{ ...s.card }}>
            <div style={{ padding:'12px 16px', borderBottom:'0.5px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontSize:13, fontWeight:600 }}>Recent clients</div>
              <div style={{ fontSize:11, color:'#94A3B8' }}>Persistent · from database</div>
            </div>
            {recentList.map((r, i) => (
              <div key={r.ucc} onClick={() => loadClient(r.ucc)}
                style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 16px', cursor:'pointer', borderBottom: i < recentList.length-1 ? '0.5px solid #F1F5F9':'none', transition:'background .15s' }}
                onMouseEnter={e => e.currentTarget.style.background='#F8FAFF'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'#EEF2FF', color:'#1B4FD8', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {(r.client_name||r.ucc)[0]}
                  </div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500 }}>{r.client_name || r.ucc}</div>
                    <div style={{ fontSize:11, color:'#64748B' }}>UCC: {r.ucc}</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:10, color:'#94A3B8' }}>{fmtIST(r.last_accessed)}</span>
                  <span style={{ fontSize:11, color:'#1B4FD8', fontWeight:500 }}>→ Load</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {loadingRecent && (
          <div style={{ textAlign:'center', color:'#94A3B8', fontSize:12, padding:20 }}>Loading recent clients…</div>
        )}
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════════════
     CLIENT VIEW
  ══════════════════════════════════════════════════════════════════════════*/
  const client   = clientData?.client   || {};
  const orders   = clientData?.orders   || [];
  const dayPos   = clientData?.day_positions || [];
  const bfPos    = clientData?.bf_positions  || [];
  const holdings = clientData?.holdings      || [];

  // Net positions tab = FAO ONLY (per spec: EQ sq-off is via Day positions tab)
  // FIX (found via live test, UCC 99901179 / SENSEX 79000 CE): the TRUE net
  // position must combine B/F carried-forward qty with today's day_positions
  // qty, exactly like Dashboard.jsx's "Net position merge" does. Previously
  // this only read day_positions on its own -- a client with a 347-lot B/F
  // long who sold 347 lots today (fully closing it) appeared here as an open
  // -347 short needing square-off, while the client's own dashboard correctly
  // showed net 0 / no open position for the same contract. Merge logic below
  // is a direct port of Dashboard.jsx's mergeKey/netMap approach so both views
  // compute the same number from the same two sources (bf_positions + day_positions).
  const dayFOOnly  = dayPos.filter(p => p.instrument_type === 'OPTIONS' || p.instrument_type === 'FUTURES');
  const bfOptsOnly = bfPos.filter(p => (p.instrument_type||'').toUpperCase() === 'OPTIONS');
  const bfFutsOnly = bfPos.filter(p => (p.instrument_type||'').toUpperCase() === 'FUTURES');

  const mergeKey = (sym, exp, str, opt) => `${sym}|${(exp||'').slice(0,10)}|${str||''}|${opt||''}`;
  const netMap = new Map();
  [...bfOptsOnly, ...bfFutsOnly].forEach(pos => {
    const key     = mergeKey(pos.symbol, pos.expiry_date, pos.strike_price, pos.option_type);
    const rawNet  = Number(pos.display_qty ?? pos.qty_in_lots ?? pos.total_open_qty ?? 0);
    const lngQty  = Number(pos.opng_lng_qty  ?? 0);
    const shrtQty = Number(pos.opng_shrt_qty ?? 0);
    const bfBuy   = lngQty  > 0 ? lngQty  : (rawNet > 0 ? rawNet  : 0);
    const bfSell  = shrtQty > 0 ? shrtQty : (rawNet < 0 ? -rawNet : 0);
    if (!netMap.has(key)) netMap.set(key, { symbol:pos.symbol, exchange:pos.exchange, segment:pos.segment||'FO', expiry_date:pos.expiry_date, strike_price:pos.strike_price, option_type:pos.option_type, instrument_type:pos.instrument_type, buy_qty:bfBuy, sell_qty:bfSell });
    else { const e = netMap.get(key); e.buy_qty += bfBuy; e.sell_qty += bfSell; }
  });
  dayFOOnly.forEach(pos => {
    const key  = mergeKey(pos.symbol, pos.expiry_date, pos.strike_price, pos.option_type);
    const dBuy = Number(pos.buy_qty) || 0, dSell = Number(pos.sell_qty) || 0;
    if (!netMap.has(key)) netMap.set(key, { symbol:pos.symbol, exchange:pos.exchange, segment:pos.segment||'FO', expiry_date:pos.expiry_date, strike_price:pos.strike_price, option_type:pos.option_type, instrument_type:pos.instrument_type, buy_qty:dBuy, sell_qty:dSell });
    else { const e = netMap.get(key); e.buy_qty += dBuy; e.sell_qty += dSell; }
  });
  // Include flat (net_qty=0) F&O rows too, matching Client's Dashboard.jsx —
  // a contract bought and sold today (net=0) still had real activity and should
  // be visible (just with a disabled "not required" action, not omitted entirely).
  const positions = Array.from(netMap.values())
    .map(p => ({ ...p, net_qty: p.buy_qty - p.sell_qty }))
    .filter(p => p.buy_qty > 0 || p.sell_qty > 0);

  const dayFO    = dayPos.filter(p => p.instrument_type !== 'EQUITY');
  const dayEQ    = dayPos.filter(p => p.instrument_type === 'EQUITY');
  const bfOpts   = bfPos.filter(p => p.instrument_type === 'OPTIONS');
  const bfFuts   = bfPos.filter(p => p.instrument_type === 'FUTURES');

  // NEW: gather the currently square-off-eligible rows for a tab and open the
  // batch confirmation modal. Recomputed fresh every click (reads positions/
  // dayEQ + latest server-driven order state via netRowAction/dayEqRowAction),
  // so it can never fire against a stale snapshot of what's actionable.
  function openBulkConfirm(kind) {
    const source = kind === 'net' ? positions : dayEQ;
    const rows = source
      .map(p => (kind === 'net' ? netRowAction(p) : dayEqRowAction(p)))
      .filter(a => a.type === 'squareOff');
    if (rows.length === 0) return;
    setBulkConfirmModal({ kind, rows });
  }

  // NEW: places every row's square-off individually against the existing,
  // unmodified /api/dealer/place-squareoff endpoint -- no new backend route,
  // so none of that endpoint's duplicate-check / segment-check / baseline-qty
  // / RMS-alert behavior is touched. Requests run with a small bounded
  // concurrency pool (3 in flight at a time) rather than fully sequential
  // (slow -- N round trips back to back) or fully parallel (risks piling up
  // concurrent duplicate-check/baseline-qty reads for the same UCC and
  // flooding the RMS email queue all at once). Each order resolves
  // independently: one failure (e.g. a duplicate raced in from elsewhere)
  // is recorded and skipped without blocking or rolling back the rest.
  async function runBulkSquareOff(kind, rows) {
    setBulkConfirmModal(null);
    setBulkPlacing(b => ({ ...b, [kind]: true }));
    setBulkProgress(b => ({ ...b, [kind]: { done: 0, total: rows.length } }));

    const CONCURRENCY = 3;
    let idx = 0;
    let successCount = 0;
    let failCount = 0;
    const failedSymbols = [];
    let expired = false;

    async function worker() {
      while (idx < rows.length && !expired) {
        const myIdx = idx++;
        const { order } = rows[myIdx];
        try {
          const r = await fetch('/api/dealer/place-squareoff', {
            method: 'POST',
            headers: authHeader(),
            body: JSON.stringify({
              ucc:          clientData.client.ucc,
              exchange:     order.exchange,
              segment:      order.segment,
              symbol:       order.symbol,
              quantity:     order.quantity,
              side:         order.side,
              expiry_date:  order.expiry_date  || null,
              strike_price: order.strike_price || null,
              option_type:  order.option_type  || null,
            })
          });
          if (r.status === 401) { expired = true; handleExpiry(); return; }
          const d = await r.json();
          if (d.success) {
            successCount++;
            // Same optimistic-flag pattern as the single square-off flow
            // above -- flips the row instantly without waiting on the
            // trailing refreshClientData() below.
            if (kind === 'net') setPlacedFO(prev => ({ ...prev, [order.foKey]: true }));
            else setPlacedDayEquity(prev => ({ ...prev, [order.dayKey]: true }));
          } else {
            failCount++;
            failedSymbols.push(order.symbol);
          }
        } catch (err) {
          failCount++;
          failedSymbols.push(order.symbol);
        } finally {
          setBulkProgress(b => ({ ...b, [kind]: { done: (b[kind]?.done || 0) + 1, total: rows.length } }));
        }
      }
    }

    const workerCount = Math.min(CONCURRENCY, rows.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    setBulkPlacing(b => ({ ...b, [kind]: false }));
    setBulkProgress(b => ({ ...b, [kind]: null }));

    if (!expired) {
      setMessage(
        failCount === 0
          ? `Square off all: ${successCount} order(s) placed successfully.`
          : `Square off all: ${successCount} placed, ${failCount} failed${failedSymbols.length ? ` (${failedSymbols.slice(0,3).join(', ')}${failedSymbols.length>3?'…':''})` : ''}.`
      );
      setMsgType(successCount > 0 ? 'success' : 'error');
      setTimeout(() => setMessage(''), 8000);
      // One reconciling refresh after the whole batch settles -- not one per
      // row, which would fire N heavy /client-data requests racing each other.
      refreshClientData();
    }
  }

  return (
    <div style={s.page}>

      {/* Confirm modal */}
      {confirmModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={{ padding:'16px 20px', borderBottom:'0.5px solid #E2E8F0' }}>
              <div style={{ fontSize:15, fontWeight:600 }}>Confirm square-off</div>
              <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>Placing on behalf of {client.client_name} ({client.ucc})</div>
            </div>
            <div style={{ padding:'16px 20px' }}>
              {[
                ['Symbol',   confirmModal.symbol + (confirmModal.strike_price ? ` ${formatStrike(confirmModal.strike_price)} ${confirmModal.option_type||''}` : '')],
                ['Exchange', `${confirmModal.exchange} / ${confirmModal.segment}`],
                ['Side',     confirmModal.side],
                ['Quantity', confirmModal.quantity],
                ['Order',    'MARKET'],
              ].map(([l,v],i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'0.5px solid #F1F5F9' }}>
                  <span style={{ fontSize:12, color:'#64748B' }}>{l}</span>
                  <span style={{ fontSize:12, fontWeight:500, color: l==='Side'?'#DC2626':'#0D0F12' }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop:12, background:'#FEF2F2', border:'0.5px solid #FECACA', borderRadius:6, padding:'8px 12px', fontSize:11, color:'#991B1B' }}>
                Market order — cannot be cancelled after placing.
              </div>
            </div>
            <div style={{ padding:'12px 20px', borderTop:'0.5px solid #E2E8F0', display:'flex', gap:10 }}>
              <button style={{ flex:1, height:38, background:'#fff', border:'0.5px solid #E2E8F0', borderRadius:8, fontSize:12, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }} onClick={()=>setConfirmModal(null)} disabled={placing}>Cancel</button>
              <button style={{ flex:2, height:38, background:'#DC2626', border:'none', borderRadius:8, color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }} onClick={placeSquareOff} disabled={placing}>
                {placing ? 'Placing…' : 'Confirm square-off'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: batch confirm modal for "Square off all" -- same visual
          language/overlay/card as the single confirmModal above, listing
          every affected row so the dealer sees exactly what's about to be
          placed before committing, preserving the existing "explicit
          confirmation before every square-off" rule at batch level. */}
      {bulkConfirmModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={{ padding:'16px 20px', borderBottom:'0.5px solid #E2E8F0' }}>
              <div style={{ fontSize:15, fontWeight:600 }}>Confirm square off all</div>
              <div style={{ fontSize:11, color:'#64748B', marginTop:2 }}>
                Placing {bulkConfirmModal.rows.length} order{bulkConfirmModal.rows.length===1?'':'s'} on behalf of {client.client_name} ({client.ucc})
              </div>
            </div>
            <div style={{ padding:'16px 20px', maxHeight:260, overflowY:'auto' }}>
              {bulkConfirmModal.rows.map((r, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'0.5px solid #F1F5F9' }}>
                  <span style={{ fontSize:12, color:'#374151' }}>
                    {r.order.symbol}{r.order.strike_price ? ` ${formatStrike(r.order.strike_price)} ${r.order.option_type||''}` : ''}
                  </span>
                  <span style={{ fontSize:12, fontWeight:600, color: r.order.side==='SELL'?'#DC2626':'#059669' }}>
                    {r.order.side} {r.order.quantity}
                  </span>
                </div>
              ))}
              <div style={{ marginTop:12, background:'#FEF2F2', border:'0.5px solid #FECACA', borderRadius:6, padding:'8px 12px', fontSize:11, color:'#991B1B' }}>
                All market orders — cannot be cancelled after placing. Any order that fails (e.g. already placed from another session) is skipped without affecting the rest.
              </div>
            </div>
            <div style={{ padding:'12px 20px', borderTop:'0.5px solid #E2E8F0', display:'flex', gap:10 }}>
              <button style={{ flex:1, height:38, background:'#fff', border:'0.5px solid #E2E8F0', borderRadius:8, fontSize:12, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}
                onClick={() => setBulkConfirmModal(null)}>
                Cancel
              </button>
              <button style={{ flex:2, height:38, background:'#DC2626', border:'none', borderRadius:8, color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}
                onClick={() => runBulkSquareOff(bulkConfirmModal.kind, bulkConfirmModal.rows)}>
                Confirm all ({bulkConfirmModal.rows.length})
              </button>
            </div>
          </div>
        </div>
      )}

      <Topbar/>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'18px 20px' }}>

        {/* Back + client info bar */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <button onClick={() => { setView('search'); setClientData(null); setMessage(''); setPlacedFO({}); setPlacedDayEquity({}); setPlacedHoldings({}); }}
              style={{ background:'none', border:'0.5px solid #E2E8F0', borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", color:'#374151', display:'flex', alignItems:'center', gap:4 }}>
              ← Back to search
            </button>
            <div style={{ fontSize:13, fontWeight:600 }}>{client.client_name}</div>
            <div style={{ background:'#EEF2FF', color:'#1B4FD8', border:'0.5px solid #C7D2FE', borderRadius:4, padding:'2px 9px', fontSize:11, fontWeight:600 }}>
              UCC: {client.ucc}
            </div>
            <div style={{ background:'#FFF7ED', color:'#C2410C', border:'0.5px solid #FED7AA', borderRadius:4, padding:'2px 9px', fontSize:11, fontWeight:600 }}>
              DEALER: {clientData?.dealerId}
            </div>
            <div style={{ background:'#F1F5F9', color:'#475569', border:'0.5px solid #CBD5E1', borderRadius:4, padding:'2px 9px', fontSize:11, fontWeight:600 }}>
              TERMINAL: {client.terminal || '—'}
            </div>
          </div>
          <button onClick={() => refreshClientData(false)} disabled={loadingClient}
            style={{ background:'#EEF2FF', color:'#1B4FD8', border:'0.5px solid #C7D2FE', borderRadius:7, padding:'6px 14px', fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", display:'flex', alignItems:'center', gap:6 }}>
            {loadingClient ? '…' : '↻'} Refresh
          </button>
        </div>

        {/* Message banner */}
        {message && (
          <div style={{ background:msgType==='success'?'#E1F5EE':'#FEF2F2', border:`0.5px solid ${msgType==='success'?'#1D9E75':'#DC2626'}`, color:msgType==='success'?'#085041':'#991B1B', borderRadius:8, padding:'9px 14px', fontSize:12, marginBottom:12 }}>
            {message}
          </div>
        )}

        {/* Tab bar */}
        <nav style={s.tabRow}>
          {[
            { key:'positions', label:`Net (${positions.length})` },
            { key:'day',       label:`Day (${dayPos.length})` },
            { key:'bf',        label:`B/F (${bfPos.length})` },
            { key:'holdings',  label:`Holdings (${holdings.length})` },
            { key:'orders',    label:`Orders (${orders.length})` },
          ].map((t,i) => (
            <button key={t.key} style={{ ...s.tabPill(activeTab===t.key), borderRight: i<4?'0.5px solid #E2E8F0':'none' }}
              onClick={()=>setActiveTab(t.key)}>{t.label}</button>
          ))}
        </nav>

        {/* ── NET POSITIONS ──────────────────────────────────────────────── */}
        {activeTab === 'positions' && (
          <div style={s.card}>
            {positions.length === 0
              ? <div style={{ padding:40, textAlign:'center', color:'#94A3B8' }}>No open net positions found for today.</div>
              : <>
                  {/* NEW: Square off all toolbar -- only rendered once there is
                      at least one actionable row, reusing the same
                      section-header visual pattern already used elsewhere in
                      this file (e.g. the Day tab's "EQUITY — Cash Market" bar
                      below) so nothing about the existing table markup moves. */}
                  <div style={s.sectionBar}>
                    <span style={{ fontSize:11, fontWeight:600, color:'#374151' }}>F&amp;O — Net Position</span>
                    {(() => {
                      const actionableCount = positions.filter(p => netRowAction(p).type === 'squareOff').length;
                      if (actionableCount === 0) return null;
                      const busy = bulkPlacing.net;
                      return (
                        <button
                          onClick={() => openBulkConfirm('net')}
                          disabled={busy}
                          style={{ ...s.sqBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
                          {busy
                            ? `Placing ${bulkProgress.net?.done ?? 0}/${bulkProgress.net?.total ?? actionableCount}…`
                            : `Square off all (${actionableCount})`}
                        </button>
                      );
                    })()}
                  </div>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:620 }}>
                      <thead><tr>
                        {['Symbol & Strike','Expiry','Type','Buy qty','Sell qty','Net qty','Exchange','Action'].map(h=><th key={h} style={s.th}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {positions.slice().sort((a,b)=>netPositionPriority(a)-netPositionPriority(b)).map((p,i) => {
                          const netQty = Number(p.net_qty);
                          return (
                            <tr key={i}>
                              <td style={{ ...s.td, fontWeight:600 }}>
                                {p.symbol}
                                {p.strike_price ? <span style={{fontSize:10,color:'#64748B',marginLeft:4}}>{formatStrike(p.strike_price)} {p.option_type||''}</span> : null}
                              </td>
                              <td style={{...s.td,fontSize:11,color:'#64748B'}}>
                                {p.expiry_date ? new Date(p.expiry_date).toLocaleDateString('en-IN') : '—'}
                              </td>
                              <td style={s.td}>{p.option_type || 'FUT'}</td>
                              <td style={{ ...s.td, color:'#059669' }}>{fmtQty(p.buy_qty)}</td>
                              <td style={{ ...s.td, color:'#DC2626' }}>{fmtQty(p.sell_qty)}</td>
                              <td style={{ ...s.td, fontWeight:600, color:netQty>0?'#059669':netQty<0?'#DC2626':'#64748B' }}>{netQty}</td>
                              <td style={s.td}><ExBadge exchange={p.exchange} segment={p.segment}/></td>
                              <td style={s.td}>
                                {(() => {
                                  const action = netRowAction(p);
                                  if (action.type === 'traded')
                                    return <span style={{...s.placed, background:'#E1F5EE', color:'#085041', borderColor:'#1D9E75'}}>Traded</span>;
                                  if (action.type === 'noOpen')
                                    return <button disabled style={s.flatDisabled}>No open position</button>;
                                  if (action.type === 'partiallyTraded')
                                    return <span style={{...s.placed, background:'#FFFBEB', color:'#92400E', borderColor:'#D97706'}}>Partially traded</span>;
                                  if (action.type === 'orderReceived')
                                    return <span style={s.placed}>Order received</span>;
                                  if (action.type === 'placed')
                                    return <span style={s.placed}>Order placed</span>;
                                  return (
                                    <button style={s.sqBtn} onClick={() => setConfirmModal({ ...action.order })}>
                                      Square off
                                    </button>
                                  );
                                })()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>}
          </div>
        )}

        {/* ── DAY POSITIONS ─────────────────────────────────────────────── */}
        {activeTab === 'day' && (
          <div>
            {dayPos.length === 0
              ? <div style={{ ...s.card, padding:40, textAlign:'center', color:'#94A3B8' }}>No day positions.</div>
              : <>
                  {dayEQ.length > 0 && (
                    <div style={s.card}>
                      <div style={s.sectionBar}>
                        <span style={{ fontSize:11, fontWeight:600, color:'#374151' }}>EQUITY — Cash Market</span>
                        {/* NEW: Square off all toolbar for Day/Equity, same
                            pattern as the Net tab above. */}
                        {(() => {
                          const actionableCount = dayEQ.filter(p => dayEqRowAction(p).type === 'squareOff').length;
                          if (actionableCount === 0) return null;
                          const busy = bulkPlacing.day;
                          return (
                            <button
                              onClick={() => openBulkConfirm('day')}
                              disabled={busy}
                              style={{ ...s.sqBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
                              {busy
                                ? `Placing ${bulkProgress.day?.done ?? 0}/${bulkProgress.day?.total ?? actionableCount}…`
                                : `Square off all (${actionableCount})`}
                            </button>
                          );
                        })()}
                      </div>
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead><tr>{['Symbol','Buy qty','Sell qty','Net qty','Exchange','Action'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {dayEQ.slice().sort((a,b)=>dayEquityPriority(a)-dayEquityPriority(b)).map((p,i) => (
                              <tr key={i}>
                                <td style={{ ...s.td, fontWeight:600 }}>{p.symbol}</td>
                                <td style={{ ...s.td, color:'#059669' }}>{fmtQty(p.buy_qty)}</td>
                                <td style={{ ...s.td, color:'#DC2626' }}>{fmtQty(p.sell_qty)}</td>
                                <td style={{ ...s.td, fontWeight:500 }}>{fmtQty(p.net_qty)}</td>
                                <td style={s.td}><ExBadge exchange={p.exchange} segment={p.segment}/></td>
                                <td style={s.td}>
                                  {(() => {
                                    const action = dayEqRowAction(p);
                                    if (action.type === 'traded')
                                      return <span style={{...s.placed, background:'#E1F5EE', color:'#085041', borderColor:'#1D9E75'}}>Traded</span>;
                                    if (action.type === 'noOpen')
                                      return <button disabled style={s.flatDisabled}>No open position</button>;
                                    if (action.type === 'partiallyTraded')
                                      return <span style={{...s.placed, background:'#FFFBEB', color:'#92400E', borderColor:'#D97706'}}>Partially traded</span>;
                                    if (action.type === 'orderReceived')
                                      return <span style={s.placed}>Order received</span>;
                                    if (action.type === 'placed')
                                      return <span style={s.placed}>Order placed</span>;
                                    return <button style={s.sqBtn} onClick={() => setConfirmModal({ ...action.order })}>Square off</button>;
                                  })()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {dayFO.length > 0 && (
                    <div style={s.card}>
                      <div style={{ padding:'9px 14px', borderBottom:'0.5px solid #E2E8F0', fontSize:11, fontWeight:600, color:'#374151' }}>F&O — View only</div>
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:560 }}>
                          <thead><tr>{['Symbol','Expiry','Type','Buy qty','Sell qty','Net qty','Exchange'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {dayFO.map((p,i) => (
                              <tr key={i}>
                                <td style={{ ...s.td, fontWeight:600 }}>{p.symbol}{p.strike_price?` ${formatStrike(p.strike_price)}`:''}</td>
                                <td style={{ ...s.td, fontSize:11, color:'#64748B' }}>{p.expiry_date?new Date(p.expiry_date).toLocaleDateString('en-IN'):'—'}</td>
                                <td style={s.td}>{p.option_type||'FUT'}</td>
                                <td style={{ ...s.td, color:'#059669' }}>{fmtQty(p.buy_qty)}</td>
                                <td style={{ ...s.td, color:'#DC2626' }}>{fmtQty(p.sell_qty)}</td>
                                <td style={{ ...s.td, fontWeight:500 }}>{fmtQty(p.net_qty)}</td>
                                <td style={s.td}><ExBadge exchange={p.exchange} segment={p.segment}/></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>}
          </div>
        )}

        {/* ── B/F POSITIONS ─────────────────────────────────────────────── */}
        {activeTab === 'bf' && (
          <div>
            {bfPos.length === 0
              ? <div style={{ ...s.card, padding:40, textAlign:'center', color:'#94A3B8' }}>No B/F positions.</div>
              : <>
                  {bfOpts.length > 0 && (
                    <div style={s.card}>
                      <div style={{ padding:'9px 14px', borderBottom:'0.5px solid #E2E8F0', fontSize:11, fontWeight:600 }}>Options B/F</div>
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:480 }}>
                          <thead><tr>{['Symbol','Expiry','Strike','Type','Qty','Exchange'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {bfOpts.map((p,i) => (
                              <tr key={i}>
                                <td style={{ ...s.td, fontWeight:600 }}>{p.symbol}</td>
                                <td style={{ ...s.td, fontSize:11 }}>{p.expiry_date?new Date(p.expiry_date).toLocaleDateString('en-IN'):'—'}</td>
                                <td style={s.td}>{p.strike_price||'—'}</td>
                                <td style={s.td}>{p.option_type}</td>
                                <td style={{ ...s.td, fontWeight:500 }}>{p.display_qty??p.qty_in_lots??p.total_open_qty??0}</td>
                                <td style={s.td}><ExBadge exchange={p.exchange} segment={p.segment}/></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {bfFuts.length > 0 && (
                    <div style={s.card}>
                      <div style={{ padding:'9px 14px', borderBottom:'0.5px solid #E2E8F0', fontSize:11, fontWeight:600 }}>Futures B/F</div>
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                          <thead><tr>{['Symbol','Expiry','Qty','Exchange'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {bfFuts.map((p,i) => (
                              <tr key={i}>
                                <td style={{ ...s.td, fontWeight:600 }}>{p.symbol}</td>
                                <td style={{ ...s.td, fontSize:11 }}>{p.expiry_date?new Date(p.expiry_date).toLocaleDateString('en-IN'):'—'}</td>
                                <td style={{ ...s.td, fontWeight:500 }}>{p.display_qty??p.qty_in_lots??p.total_open_qty??0}</td>
                                <td style={s.td}><ExBadge exchange={p.exchange} segment={p.segment}/></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>}
          </div>
        )}

        {/* ── HOLDINGS ──────────────────────────────────────────────────── */}
        {activeTab === 'holdings' && (
          <div style={s.card}>
            {holdings.length === 0
              ? <div style={{ padding:40, textAlign:'center', color:'#94A3B8' }}>No holdings found.</div>
              : <>
                  <div style={{ padding:'9px 14px', borderBottom:'0.5px solid #E2E8F0', fontSize:11, fontWeight:600 }}>NSDL Holdings · View only</div>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:480 }}>
                      <thead><tr>{['Company','Qty','Price','Value','Action'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {holdings.map((h,i) => (
                          <tr key={i}>
                            <td style={{ ...s.td, fontWeight:600, fontSize:11 }}>{h.company_name}</td>
                            <td style={s.td}>{h.quantity}</td>
                            <td style={{ ...s.td, color:'#64748B' }}>{h.close_price?`₹${Number(h.close_price).toFixed(2)}`:'—'}</td>
                            <td style={{ ...s.td, color:'#1D9E75', fontWeight:500 }}>{h.total_value?`₹${Number(h.total_value).toLocaleString('en-IN',{maximumFractionDigits:0})}`:'—'}</td>
                            <td style={s.td}>
                              {/* FIX (2026-07-20): Holdings now shows the same Traded/
                                  Partially traded pill the Day/Net/Orders tabs already
                                  use (via backend today_status, per-ISIN, same
                                  file_generated compliance gate as everywhere else) --
                                  previously this row only ever showed "Order received"
                                  (from isPlaced/placedHoldings, both reset on reload) or
                                  a bare "Square off" button, so once RMS actually
                                  executed the trade the dealer had no visible signal,
                                  only a silently-changed quantity number. */}
                              {!isEquityHolding(h.company_name, h.isin)
                                ? <button disabled style={s.flatDisabled}>View only</button>
                                : h.today_status === 'TRADED'
                                  ? <span style={{...s.placed,background:'#E1F5EE',color:'#085041',border:'0.5px solid #1D9E75'}}>Traded</span>
                                : h.today_status === 'PARTIALLY_TRADED'
                                  ? <span style={{...s.placed,background:'#FFFBEB',color:'#92400E',border:'0.5px solid #D97706'}}>Partially traded</span>
                                : (h.today_status === 'ORDER_RECEIVED' || isPlaced(`${h.company_name} ; ${h.isin}`,'NSE','CM') || placedHoldings[h.isin])
                                  ? <span style={{...s.placed,background:'#EEF2FF',color:'#1338A8',border:'0.5px solid #BFDBFE'}}>Order received</span>
                                  : <button style={s.sqBtn} onClick={()=>setConfirmModal({ symbol:`${h.company_name} ; ${h.isin}`, exchange:'NSE', segment:'CM', side:'SELL', quantity:h.quantity, isHolding:true, isin:h.isin })}>Square off</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>}
          </div>
        )}

        {/* * ── ORDERS ────────────────────────────────────────────────────── */}
        {activeTab === 'orders' && (
          <div style={s.card}>
            {orders.length === 0
              ? <div style={{ padding:40, textAlign:'center', color:'#94A3B8' }}>No orders placed yet in this session.</div>
              : <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                    <thead><tr>
                      {/* Column set now mirrors client Dashboard.jsx's Orders tab exactly
                          (Placed Qty / Traded Qty split, Placed On / Traded On split) --
                          previously this table showed only a combined Qty + single
                          Date & time column, so dealers couldn't see executed quantity
                          or the actual trade timestamp the way clients could. Traded
                          Price added (16-Jul-2026) to mirror the price already sent to
                          clients via email/WhatsApp alerts, mapped from the same
                          o.trade_price field the client view uses. */}
                      {['Symbol','Exchange','Type','Placed Qty','Traded Qty','Traded Price','Status','Placed By','Placed On','Traded On'].map(h=><th key={h} style={s.th}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {orders.map((o,i) => {
                        const sb = statusBadge(o.status);
                        // Traded On: time-only, matching client Dashboard.jsx's tradedTimeFmt
                        const tradedTimeFmt = o.traded_at ? new Date(o.traded_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true}) : '—';
                        return (
                          <tr key={o.order_id||i}>
                            <td style={{ ...s.td, fontWeight:600 }}>
                              {o.symbol}{o.strike_price?` ${formatStrike(o.strike_price)} ${o.option_type||''}` :''}
                              {o.expiry_date?` · ${new Date(o.expiry_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}`:''}
                            </td>
                            <td style={s.td}>{o.exchange}/{o.segment}</td>
                            <td style={{ ...s.td, color:o.side==='SELL'?'#DC2626':'#059669', fontWeight:600 }}>{o.side}</td>
                            <td style={s.td}>{o.quantity}</td>
                            <td style={s.td}>{o.traded_at ? (o.executed_qty ?? '—') : '—'}</td>
                            <td style={s.td}>{o.traded_at ? fmtPrice(o.trade_price) : '—'}</td>
                            <td style={s.td}>
                              <span style={{ ...s.statusPill, background:sb.bg, color:sb.col }}>{sb.label}</span>
                              {/* Stale: this order's target position already went flat
                                  via some other route (client's own app, duplicate/test
                                  order, etc) before RMS processed it -- same signal the
                                  admin's Sq-Off Orders table now shows. Backend sets
                                  is_stale in dealerAuth.js's /client-data orders query. */}
                              {!!o.is_stale && (
                                <span title="This order's target position is already flat (net qty = 0) -- it likely closed another way before RMS processed this order."
                                  style={{ display:'block', marginTop:4, fontSize:9, fontWeight:600, padding:'2px 6px', borderRadius:4, background:'#fef2f2', color:'#991b1b', border:'0.5px solid #fecaca', cursor:'help' }}>
                                  ⚠ Stale
                                </span>
                              )}
                            </td>
                            <td style={{ ...s.td, fontSize:11, whiteSpace:'nowrap' }}>{o.placed_by||'CLIENT'}{o.dealer_id?` (${o.dealer_id})`:''}</td>
                            <td style={{ ...s.td, fontSize:11, whiteSpace:'nowrap' }}>{fmtIST(o.placed_at)}</td>
                            <td style={{ ...s.td, fontSize:11, whiteSpace:'nowrap' }}>{tradedTimeFmt}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>}
          </div>
        )}
      </div>
    </div>
  );
}